import {
  GlobalOutlined,
  MailOutlined,
  NodeIndexOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import { AppPaths } from "@p0security/shared/routes/constants";
import { assertNever } from "@p0security/shared/types/index";
import {
  AgenticRequestorRule,
  RequestorRule,
} from "@p0security/shared/types/policy/types";
import { AgenticClientsContext } from "components/Agentic/AgenticClientsContext";
import { AgenticPaths } from "components/Agentic/paths";
import { StyledRoutingRulePreview } from "components/Jit/Policy/styles";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import type { ReactNode } from "react";
import { useContext } from "react";
import { Link } from "react-router-dom";

import { stopPropagation } from "../../../../../utils/javascript.ts";
import { IdpGroupsPreview, RequestorIdpGroupPreview } from "./IdpGroupPreview";

type AgentNameOptions = { upperFirst?: boolean };

/** Renders an MCP client as its human-friendly name linking to its detail page.
 *
 * Falls back to the raw client id when the name has not loaded (or when
 * rendered outside an AgenticClientsProvider).
 */
const AgentClientName: React.FC<{
  clientId: string;
  options?: AgentNameOptions;
}> = ({ clientId, options }) => {
  const { clientsById } = useContext(AgenticClientsContext);
  const name = clientsById.get(clientId)?.displayName || clientId;
  const a = options?.upperFirst ? "A" : "a";
  return (
    <span onClick={stopPropagation}>
      {a}gent{" "}
      <TenantAwareLink
        to={`${AppPaths.AgenticAccess}/${AgenticPaths.Clients}/${encodeURIComponent(clientId)}`}
      >
        {name}
      </TenantAwareLink>
    </span>
  );
};

export type RequestorRulePreview =
  | RequestorRule
  | {
      type: "genericGroup";
    };

type Props = {
  requestor: RequestorRulePreview;
};

const agentSummary = (
  agent: AgenticRequestorRule["actor"] | undefined,
  options?: AgentNameOptions
) => {
  const a = options?.upperFirst ? "A" : "a";
  if (!agent) return `${a}n agent`;
  switch (agent.type) {
    case "any":
      return `${a}ny agent`;
    case "agent-client":
      return agent.clientId ? (
        <AgentClientName clientId={agent.clientId} options={options} />
      ) : (
        `${a}ny agent`
      );
    case "agent-owner":
      return agent.owner
        ? `${a}gent owned by ${agent.owner}`
        : `${a}gent by owner`;
    case "owner-group":
      return (
        <IdpGroupsPreview
          prefix="agent owned by members"
          effect={agent.groups.effect}
          idpGroups={agent.groups.groups}
        />
      );
    case "provider":
      return (
        <span onClick={stopPropagation}>
          {a}gent issued by{" "}
          <Link
            to={{
              pathname: `../integrations/identity-provider/provider/${itemRouteSegment(agent.providerId)}`,
            }}
          >
            {agent.providerId}
          </Link>
        </span>
      );
    case "same-as-subject":
      // Should never be shown; omitted in headless path
      return "";
    default:
      return assertNever(agent);
  }
};

const agenticUserSummary = (
  user: AgenticRequestorRule["subject"] | undefined,
  options?: AgentNameOptions
): ReactNode => {
  if (!user) return "An identity";
  switch (user.type) {
    case "any":
      return "Any user";
    case "user":
      switch (user.user.type) {
        case "any":
          return "Any user";
        case "user":
          return user.user.uid || "a specific user";
        case "group":
          return (
            <IdpGroupsPreview
              prefix="Identities"
              idpGroups={user.user.groups}
              effect={user.user.effect}
            />
          );
        default:
          return "An identity";
      }
    case "agent":
      return agentSummary(user.agent, options);
    default:
      return "An identity";
  }
};

export const RequestorPreview: React.FC<Props> = ({ requestor }) => {
  if (requestor.type === "genericGroup") return <RequestorIdpGroupPreview />;
  switch (requestor.type) {
    case "any":
      return (
        <StyledRoutingRulePreview>
          <GlobalOutlined />
          <span>Any user</span>
        </StyledRoutingRulePreview>
      );
    case "user":
      return (
        <StyledRoutingRulePreview>
          <MailOutlined />
          <span>{requestor.uid || "Specific user"}</span>
        </StyledRoutingRulePreview>
      );
    case "group":
      return (
        <StyledRoutingRulePreview>
          <TeamOutlined />
          <IdpGroupsPreview
            idpGroups={requestor.groups}
            prefix="User"
            effect={requestor.effect}
          />
        </StyledRoutingRulePreview>
      );
    case "agentic":
      // A "same-as-subject" actor means the initiating headless agent acts as
      // itself, so present the subject alone rather than a redundant "via" clause.
      if (requestor.actor.type === "same-as-subject")
        return (
          <StyledRoutingRulePreview>
            <NodeIndexOutlined />
            <span>Headless {agenticUserSummary(requestor.subject)}</span>
          </StyledRoutingRulePreview>
        );
      return (
        <StyledRoutingRulePreview>
          <NodeIndexOutlined />
          <span>
            {agenticUserSummary(requestor.subject, { upperFirst: true })}
          </span>
          <span className="col-span-2">
            via {agentSummary(requestor.actor)}
          </span>
        </StyledRoutingRulePreview>
      );
    default:
      return assertNever(requestor);
  }
};
